import { useGetPlatformStatus, type StatusCheckOut } from '@myrentalmate/api-client';
import { Alert, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { StatRow, StatTile } from '../../components/Stats';
import { TabbedLayout } from '../../components/TabbedLayout';
import { okData } from '../../lib/apiData';
import { formatDateTime } from '../../lib/format';

const TABS = [
  { to: '/platform/status', label: 'Health Checks' },
  { to: '/platform/status/jobs', label: 'Scheduled Jobs' },
  { to: '/platform/status/database', label: 'Database Usage' },
];

export function SystemStatusLayout() {
  return <TabbedLayout title="System Status" label="System status sections" tabs={TABS} />;
}

const STATUS: Record<
  StatusCheckOut['status'],
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  ok: { label: 'Healthy', color: 'success' },
  warning: { label: 'Check', color: 'warning' },
  error: { label: 'Problem', color: 'error' },
};

const COLUMNS: DataColumn<StatusCheckOut>[] = [
  { label: 'Check', render: (c) => c.name },
  {
    label: 'Status',
    render: (c) => (
      <Chip size="small" label={STATUS[c.status].label} color={STATUS[c.status].color} />
    ),
  },
  { label: 'Detail', render: (c) => c.detail },
];

export function SystemStatusPage() {
  const query = useGetPlatformStatus();
  const status = okData(query.data);
  const checks = status?.checks ?? [];
  const problems = checks.filter((c) => c.status === 'error').length;
  const warnings = checks.filter((c) => c.status === 'warning').length;
  return (
    <Stack spacing={2}>
      {status && (
        <Alert
          severity={problems ? 'error' : warnings ? 'warning' : 'success'}
          action={
            <Button
              size="small"
              color="inherit"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Check Again
            </Button>
          }
        >
          {problems
            ? 'Attention Needed'
            : warnings
              ? 'Operational, With Items to Check'
              : 'All Systems Operational'}
        </Alert>
      )}
      {query.isError && <Alert severity="error">Couldn&apos;t run the checks.</Alert>}
      {status && (
        <StatRow>
          <StatTile label="Problems" value={problems} />
          <StatTile label="To Check" value={warnings} />
          <StatTile label="Running Since" value={formatDateTime(status.started_at)} />
          <StatTile label="Release" value={status.release.replace(/^.*--/, '')} />
        </StatRow>
      )}
      {!status && query.isPending ? (
        <CircularProgress aria-label="Loading" />
      ) : (
        <Stack spacing={1}>
          <Typography variant="h6">Health Checks</Typography>
          <DataTable columns={COLUMNS} rows={checks} rowKey={(c) => c.name} />
        </Stack>
      )}
    </Stack>
  );
}
